var utils = require("./utils");

var CPU_FREQ_NTSC = 1789772.5; //1789772.72727272d;
// var CPU_FREQ_PAL = 1773447.4;

// Frame sequencer schedules, in CPU cycles from the moment the counter is
// reset. The sequencer itself is clocked once per APU cycle (two CPU cycles);
// these are the NESdev APU cycle numbers doubled, with the quarter/half frame
// clocks landing one CPU cycle later than the step that triggers them.
//
// Mode 0 (four step):  quarter 3728/7456/11185/14914, half 7456/14914,
//                      frame IRQ flag set over 14914 GET, 14914 PUT and
//                      14915 GET (hence the three consecutive cycles below).
// Mode 1 (five step):  quarter 3728/7456/11185/14914/18640, half 7456/18640.
var FRAME_SEQUENCES = [
  {
    quarter: [7457, 14913, 22371, 29829],
    half: [14913, 29829],
    irq: [29828, 29829, 29830],
    period: 29830,
  },
  {
    quarter: [7457, 14913, 22371, 29829, 37281],
    half: [14913, 37281],
    irq: [],
    period: 37282,
  },
];

var PAPU = function (nes) {
  this.nes = nes;

  this.square1 = new ChannelSquare(this, true);
  this.square2 = new ChannelSquare(this, false);
  this.triangle = new ChannelTriangle(this);
  this.noise = new ChannelNoise(this);
  this.dmc = new ChannelDM(this);

  this.frameIrqCounter = null;
  this.frameIrqCounterMax = 4;
  this.initCounter = 2048;
  this.channelEnableValue = null;

  this.sampleRate = 44100;

  this.lengthLookup = null;
  this.dmcFreqLookup = null;
  this.noiseWavelengthLookup = null;
  this.square_table = null;
  this.tnd_table = null;

  this.frameIrqEnabled = false;
  this.frameIrqActive = null;
  this.frameClockNow = null;
  this.startedPlaying = false;
  this.recordOutput = false;
  this.initingHardware = false;

  this.apuClock = 0;
  this.pendingFrameReset = -1;
  this.pendingFrameImmediate = false;
  this.frameEvents = [];
  this.cycleDebt = 0;
  this.countSequence = null;
  this.sampleTimer = null;
  this.sampleTimerMax = null;
  this.sampleCount = null;
  this.triValue = 0;

  this.smpSquare1 = null;
  this.smpSquare2 = null;
  this.smpTriangle = null;
  this.smpDmc = null;
  this.accCount = null;

  // DC removal vars:
  this.prevSampleL = 0;
  this.prevSampleR = 0;
  this.smpAccumL = 0;
  this.smpAccumR = 0;

  // DAC range:
  this.dacRange = 0;
  this.dcValue = 0;

  // Master volume:
  this.masterVolume = 256;

  // Stereo positioning:
  this.stereoPosLSquare1 = null;
  this.stereoPosLSquare2 = null;
  this.stereoPosLTriangle = null;
  this.stereoPosLNoise = null;
  this.stereoPosLDMC = null;
  this.stereoPosRSquare1 = null;
  this.stereoPosRSquare2 = null;
  this.stereoPosRTriangle = null;
  this.stereoPosRNoise = null;
  this.stereoPosRDMC = null;

  this.extraCycles = null;

  this.maxSample = null;
  this.minSample = null;

  // Sound expansion modules registered by the mappers (VRC6, VRC7, ...):
  this.externalSounds = [];

  // Panning:
  this.panning = [80, 170, 100, 150, 128];
  this.setPanning(this.panning);

  // Initialize lookup tables:
  this.initLengthLookup();
  this.initDmcFrequencyLookup();
  this.initNoiseWavelengthLookup();
  this.initDACtables();

  // Init sound registers:
  for (var i = 0; i < 0x14; i++) {
    if (i === 0x10) {
      this.writeReg(0x4010, 0x10);
    } else {
      this.writeReg(0x4000 + i, 0);
    }
  }

  this.reset();
};

PAPU.prototype = {
  reset: function () {
    this.sampleRate = this.nes.opts.sampleRate;
    // Use precise floating-point for sample timer to avoid pitch drift.
    // CPU cycles per sample = CPU_FREQ_NTSC / sampleRate.
    // sampleTimer increments by 1024 per CPU cycle (<< 10).
    // So sampleTimerMax = 1024 * CPU_FREQ_NTSC / sampleRate.
    this.sampleTimerMax = (1024.0 * CPU_FREQ_NTSC) / this.sampleRate;

    this.sampleTimer = 0;

    this.updateChannelEnable(0);
    this.apuClock = 0;
    this.pendingFrameReset = -1;
    this.pendingFrameImmediate = false;
    this.frameEvents = [];
    this.cycleDebt = 0;
    this.countSequence = 0;
    this.sampleCount = 0;
    this.initCounter = 2048;
    this.frameIrqEnabled = false;
    this.initingHardware = false;

    this.resetCounter();

    this.square1.reset();
    this.square2.reset();
    this.triangle.reset();
    this.noise.reset();
    this.dmc.reset();

    this.accCount = 0;
    this.smpSquare1 = 0;
    this.smpSquare2 = 0;
    this.smpTriangle = 0;
    this.smpDmc = 0;

    this.frameIrqEnabled = false;
    this.frameIrqCounterMax = 4;

    this.channelEnableValue = 0xff;
    this.startedPlaying = false;
    this.prevSampleL = 0;
    this.prevSampleR = 0;
    this.smpAccumL = 0;
    this.smpAccumR = 0;

    this.maxSample = -500000;
    this.minSample = 500000;
  },

  // eslint-disable-next-line no-unused-vars
  readReg: function (address) {
    // Reading $4015 is the last cycle of the CPU's absolute access: bring the
    // frame counter up to that cycle first, otherwise the flag/length state
    // seen here is three cycles stale (the rest of the instruction is clocked
    // when it ends).
    if (address === 0x4015) {
      this.syncRegisterAccess();
    }

    // Read 0x4015:
    var tmp = 0;
    tmp |= this.square1.getLengthStatus();
    tmp |= this.square2.getLengthStatus() << 1;
    tmp |= this.triangle.getLengthStatus() << 2;
    tmp |= this.noise.getLengthStatus() << 3;
    tmp |= this.dmc.getLengthStatus() << 4;
    tmp |= (this.frameIrqActive && this.frameIrqEnabled ? 1 : 0) << 6;
    tmp |= this.dmc.getIrqStatus() << 7;

    this.frameIrqActive = false;

    return tmp & 0xffff;
  },

  writeReg: function (address, value) {
    // Same three-cycle catch-up for $4017: its divider reset must happen on
    // the cycle of the write, not at the start of the STA.
    if (address === 0x4017) {
      this.syncRegisterAccess();
    }

    if (address >= 0x4000 && address < 0x4004) {
      // Square Wave 1 Control
      this.square1.writeReg(address, value);
      // console.log("Square Write");
    } else if (address >= 0x4004 && address < 0x4008) {
      // Square 2 Control
      this.square2.writeReg(address, value);
    } else if (address >= 0x4008 && address < 0x400c) {
      // Triangle Control
      this.triangle.writeReg(address, value);
    } else if (address >= 0x400c && address <= 0x400f) {
      // Noise Control
      this.noise.writeReg(address, value);
    } else if (address === 0x4010) {
      // DMC Play mode & DMA frequency
      this.dmc.writeReg(address, value);
    } else if (address === 0x4011) {
      // DMC Delta Counter
      this.dmc.writeReg(address, value);
    } else if (address === 0x4012) {
      // DMC Play code starting address
      this.dmc.writeReg(address, value);
    } else if (address === 0x4013) {
      // DMC Play code length
      this.dmc.writeReg(address, value);
    } else if (address === 0x4015) {
      // Channel enable
      this.updateChannelEnable(value);

      if (value !== 0 && this.initCounter > 0) {
        // Start hardware initialization
        this.initingHardware = true;
      }

      // DMC/IRQ Status
      this.dmc.writeReg(address, value);
    } else if (address === 0x4017) {
      // Frame counter control. The counter does not restart on the write
      // cycle itself: the effect shows up three CPU cycles later when the
      // write lands inside an APU cycle, and four cycles later when it lands
      // between two of them. That half-cycle ambiguity is the clock jitter
      // the APU tests probe.
      this.countSequence = (value >> 7) & 1;
      // Only bit 6 clears the frame IRQ flag; $00 and $80 leave it alone.
      if (((value >> 6) & 0x1) === 1) {
        this.frameIrqActive = false;
      }

      if (((value >> 6) & 0x1) === 0) {
        this.frameIrqEnabled = true;
      } else {
        this.frameIrqEnabled = false;
      }

      this.pendingFrameReset =
        this.apuClock + ((this.apuClock & 1) !== 0 ? 3 : 4);
      this.pendingFrameImmediate = ((value >> 7) & 1) !== 0;
    }
  },

  resetCounter: function () {
    this.pendingFrameReset = -1;
    this.pendingFrameImmediate = false;
    this.startFrame(0, false);
  },

  // Updates channel enable status.
  // This is done on writes to the
  // channel enable register (0x4015),
  // and when the user enables/disables channels
  // in the GUI.
  updateChannelEnable: function (value) {
    this.channelEnableValue = value & 0xffff;
    this.square1.setEnabled((value & 1) !== 0);
    this.square2.setEnabled((value & 2) !== 0);
    this.triangle.setEnabled((value & 4) !== 0);
    this.noise.setEnabled((value & 8) !== 0);
    this.dmc.setEnabled((value & 16) !== 0);
  },

  // The CPU clocks the APU once per instruction, after it runs: during a
  // register access the APU is therefore at the start of that instruction.
  // syncRegisterAccess() clocks the three cycles that separate the start of a
  // four-cycle absolute access from its read/write cycle; the fourth cycle is
  // clocked with the rest of the instruction.
  syncRegisterAccess: function () {
    if (this.cycleDebt === 0) {
      this.clockFrameCounter(3);
      this.cycleDebt = 3;
    }
  },

  // Clocks the frame counter. It should be clocked at
  // twice the cpu speed, so the cycles will be
  // divided by 2 for those counters that are
  // clocked at cpu speed.
  clockFrameCounter: function (nCycles) {
    // Cycles already accounted for by syncRegisterAccess() must not be
    // counted twice when the instruction ends.
    var total = nCycles;
    if (this.cycleDebt > 0) {
      var debt = Math.min(this.cycleDebt, total);
      this.cycleDebt -= debt;
      total -= debt;
      nCycles -= debt;
    }

    // The absolute clock drives the frame sequencer, including while the
    // power-on initialization counter is still running.
    this.apuClock += total;
    this.processFrameEvents(this.apuClock);

    if (nCycles <= 0) {
      return;
    }

    if (this.initCounter > 0) {
      if (this.initingHardware) {
        this.initCounter -= nCycles;
        if (this.initCounter <= 0) {
          this.initingHardware = false;
        }
        return;
      }
    }

    // Sound expansion modules are clocked in step with the CPU, before any
    // sample-rate clamping so they never lose cycles.
    this.clockExternalSounds(nCycles);

    // Don't clamp nCycles to sample timer - always clock channels for the full
    // CPU cycles. The sample timer will naturally wrap and generate samples
    // as needed. Clamping nCycles caused the APU to run slow and pitch to drop.
    // this.extraCycles mechanism removed.

    var dmc = this.dmc;
    var triangle = this.triangle;
    var square1 = this.square1;
    var square2 = this.square2;
    var noise = this.noise;

    // Clock DMC:
    if (dmc.isEnabled) {
      dmc.shiftCounter -= nCycles << 3;
      while (dmc.shiftCounter <= 0 && dmc.dmaFrequency > 0) {
        dmc.shiftCounter += dmc.dmaFrequency;
        dmc.clockDmc();
      }
    }

    // Clock Triangle channel Prog timer:
    if (triangle.progTimerMax > 0) {
      triangle.progTimerCount -= nCycles;
      while (triangle.progTimerCount <= 0) {
        triangle.progTimerCount += triangle.progTimerMax + 1;
        if (triangle.linearCounter > 0 && triangle.lengthCounter > 0) {
          triangle.triangleCounter++;
          triangle.triangleCounter &= 0x1f;

          if (triangle.isEnabled) {
            if (triangle.triangleCounter >= 0x10) {
              // Normal value.
              triangle.sampleValue = triangle.triangleCounter & 0xf;
            } else {
              // Inverted value.
              triangle.sampleValue = 0xf - (triangle.triangleCounter & 0xf);
            }
            triangle.sampleValue <<= 4;
          }
        }
      }
    }

    // Clock Square channel 1 Prog timer:
    square1.progTimerCount -= nCycles;
    if (square1.progTimerCount <= 0) {
      square1.progTimerCount += (square1.progTimerMax + 1) << 1;

      square1.squareCounter++;
      square1.squareCounter &= 0x7;
      square1.updateSampleValue();
    }

    // Clock Square channel 2 Prog timer:
    square2.progTimerCount -= nCycles;
    if (square2.progTimerCount <= 0) {
      square2.progTimerCount += (square2.progTimerMax + 1) << 1;

      square2.squareCounter++;
      square2.squareCounter &= 0x7;
      square2.updateSampleValue();
    }

    // Clock noise channel Prog timer:
    var acc_c = nCycles;
    if (noise.progTimerCount - acc_c > 0) {
      // Do all cycles at once:
      noise.progTimerCount -= acc_c;
      noise.accCount += acc_c;
      noise.accValue += acc_c * noise.sampleValue;
    } else {
      // Slow-step:
      while (acc_c-- > 0) {
        if (--noise.progTimerCount <= 0 && noise.progTimerMax > 0) {
          // Update noise shift register:
          noise.shiftReg <<= 1;
          noise.tmp =
            ((noise.shiftReg << (noise.randomMode === 0 ? 1 : 6)) ^
              noise.shiftReg) &
            0x8000;
          if (noise.tmp !== 0) {
            // Sample value must be 0.
            noise.shiftReg |= 0x01;
            noise.randomBit = 0;
            noise.sampleValue = 0;
          } else {
            // Find sample value:
            noise.randomBit = 1;
            if (noise.isEnabled && noise.lengthCounter > 0) {
              noise.sampleValue = noise.masterVolume;
            } else {
              noise.sampleValue = 0;
            }
          }

          noise.progTimerCount += noise.progTimerMax;
        }

        noise.accValue += noise.sampleValue;
        noise.accCount++;
      }
    }

    // Frame IRQ handling:
    if (this.frameIrqEnabled && this.frameIrqActive) {
      this.nes.cpu.requestIrq(this.nes.cpu.IRQ_NORMAL);
    }

    // Accumulate sample value:
    this.accSample(nCycles);

    // Clock sample timer - allow multiple samples per call if timer wraps multiple times:
    this.sampleTimer += nCycles << 10;
    while (this.sampleTimer >= this.sampleTimerMax) {
      this.sample();
      this.sampleTimer -= this.sampleTimerMax;
    }
  },

  accSample: function (cycles) {
    // Special treatment for triangle channel - need to interpolate.
    if (this.triangle.sampleCondition) {
      this.triValue = Math.floor(
        (this.triangle.progTimerCount << 4) / (this.triangle.progTimerMax + 1),
      );
      if (this.triValue > 16) {
        this.triValue = 16;
      }
      if (this.triangle.triangleCounter >= 16) {
        this.triValue = 16 - this.triValue;
      }

      // Add non-interpolated sample value:
      this.triValue += this.triangle.sampleValue;
    }

    // Now sample normally:
    if (cycles === 2) {
      this.smpTriangle += this.triValue << 1;
      this.smpDmc += this.dmc.sample << 1;
      this.smpSquare1 += this.square1.sampleValue << 1;
      this.smpSquare2 += this.square2.sampleValue << 1;
      this.accCount += 2;
    } else if (cycles === 4) {
      this.smpTriangle += this.triValue << 2;
      this.smpDmc += this.dmc.sample << 2;
      this.smpSquare1 += this.square1.sampleValue << 2;
      this.smpSquare2 += this.square2.sampleValue << 2;
      this.accCount += 4;
    } else {
      this.smpTriangle += cycles * this.triValue;
      this.smpDmc += cycles * this.dmc.sample;
      this.smpSquare1 += cycles * this.square1.sampleValue;
      this.smpSquare2 += cycles * this.square2.sampleValue;
      this.accCount += cycles;
    }
  },

  // Starts a fresh 4-step or 5-step sequence at the given absolute cycle.
  // `immediate` reproduces the behaviour of bit 7 of $4017: the write also
  // clocks the quarter and half frame units once.
  startFrame: function (cycle, immediate) {
    var spec = FRAME_SEQUENCES[this.countSequence & 1];
    var events = [];
    var i;

    for (i = 0; i < spec.quarter.length; i++) {
      events.push({ at: cycle + spec.quarter[i], type: 0 });
    }
    for (i = 0; i < spec.half.length; i++) {
      events.push({ at: cycle + spec.half[i], type: 1 });
    }
    for (i = 0; i < spec.irq.length; i++) {
      events.push({ at: cycle + spec.irq[i], type: 2 });
    }
    events.push({ at: cycle + spec.period, type: 3 });
    events.sort(function (a, b) {
      return a.at - b.at || a.type - b.type;
    });
    this.frameEvents = events;

    if (immediate) {
      this.clockQuarterFrame();
      this.clockHalfFrame();
    }
  },

  // Applies every frame event whose cycle has been reached, including a
  // pending $4017 reset. Called for each slice of CPU time so the length
  // counters change on the exact cycle the tests measure.
  processFrameEvents: function (cycle) {
    for (;;) {
      var events = this.frameEvents;
      var eventAt = events.length > 0 ? events[0].at : Infinity;

      if (
        this.pendingFrameReset >= 0 &&
        this.pendingFrameReset <= cycle &&
        this.pendingFrameReset <= eventAt
      ) {
        var start = this.pendingFrameReset;
        var immediate = this.pendingFrameImmediate;
        this.pendingFrameReset = -1;
        this.pendingFrameImmediate = false;
        this.startFrame(start, immediate);
        continue;
      }

      if (eventAt > cycle) {
        break;
      }

      var event = events.shift();
      if (event.type === 3) {
        // End of the sequence: the counter wraps and the next one begins.
        this.startFrame(event.at, false);
      } else if (event.type === 0) {
        this.clockQuarterFrame();
      } else if (event.type === 1) {
        this.clockHalfFrame();
      } else if (this.countSequence === 0 && this.frameIrqEnabled) {
        this.frameIrqActive = true;
      }
    }
  },

  clockQuarterFrame: function () {
    this.square1.clockEnvDecay();
    this.square2.clockEnvDecay();
    this.noise.clockEnvDecay();
    this.triangle.clockLinearCounter();
  },

  clockHalfFrame: function () {
    this.triangle.clockLengthCounter();
    this.square1.clockLengthCounter();
    this.square2.clockLengthCounter();
    this.noise.clockLengthCounter();
    this.square1.clockSweep();
    this.square2.clockSweep();
  },

  // Samples the channels, mixes the output together, then writes to buffer.
  sample: function () {
    var sq_index, tnd_index;

    if (this.accCount > 0) {
      this.smpSquare1 <<= 4;
      this.smpSquare1 = Math.floor(this.smpSquare1 / this.accCount);

      this.smpSquare2 <<= 4;
      this.smpSquare2 = Math.floor(this.smpSquare2 / this.accCount);

      this.smpTriangle = Math.floor(this.smpTriangle / this.accCount);

      this.smpDmc <<= 4;
      this.smpDmc = Math.floor(this.smpDmc / this.accCount);

      this.accCount = 0;
    } else {
      this.smpSquare1 = this.square1.sampleValue << 4;
      this.smpSquare2 = this.square2.sampleValue << 4;
      this.smpTriangle = this.triangle.sampleValue;
      this.smpDmc = this.dmc.sample << 4;
    }

    var smpNoise = Math.floor((this.noise.accValue << 4) / this.noise.accCount);
    this.noise.accValue = smpNoise >> 4;
    this.noise.accCount = 1;

    // Stereo sound.

    // Left channel:
    sq_index =
      (this.smpSquare1 * this.stereoPosLSquare1 +
        this.smpSquare2 * this.stereoPosLSquare2) >>
      8;
    tnd_index =
      (3 * this.smpTriangle * this.stereoPosLTriangle +
        (smpNoise << 1) * this.stereoPosLNoise +
        this.smpDmc * this.stereoPosLDMC) >>
      8;
    if (sq_index >= this.square_table.length) {
      sq_index = this.square_table.length - 1;
    }
    if (tnd_index >= this.tnd_table.length) {
      tnd_index = this.tnd_table.length - 1;
    }
    var sampleValueL =
      this.square_table[sq_index] + this.tnd_table[tnd_index] - this.dcValue;

    // Right channel:
    sq_index =
      (this.smpSquare1 * this.stereoPosRSquare1 +
        this.smpSquare2 * this.stereoPosRSquare2) >>
      8;
    tnd_index =
      (3 * this.smpTriangle * this.stereoPosRTriangle +
        (smpNoise << 1) * this.stereoPosRNoise +
        this.smpDmc * this.stereoPosRDMC) >>
      8;
    if (sq_index >= this.square_table.length) {
      sq_index = this.square_table.length - 1;
    }
    if (tnd_index >= this.tnd_table.length) {
      tnd_index = this.tnd_table.length - 1;
    }
    var sampleValueR =
      this.square_table[sq_index] + this.tnd_table[tnd_index] - this.dcValue;

    // Remove DC from left channel:
    var smpDiffL = sampleValueL - this.prevSampleL;
    this.prevSampleL += smpDiffL;
    this.smpAccumL += smpDiffL - (this.smpAccumL >> 10);
    sampleValueL = this.smpAccumL;

    // Remove DC from right channel:
    var smpDiffR = sampleValueR - this.prevSampleR;
    this.prevSampleR += smpDiffR;
    this.smpAccumR += smpDiffR - (this.smpAccumR >> 10);
    sampleValueR = this.smpAccumR;

    // Write:
    if (sampleValueL > this.maxSample) {
      this.maxSample = sampleValueL;
    }
    if (sampleValueL < this.minSample) {
      this.minSample = sampleValueL;
    }

    // Mix in any cartridge sound expansion module (VRC6 for mappers 24/26, ...).
    var GAIN = 2.0;
    for (var i = 0; i < this.externalSounds.length; i++) {
      var expansionSample = this.externalSounds[i].generate();
      sampleValueL += expansionSample;
      sampleValueR += expansionSample;
    }

    if (this.nes.opts.emulateSound && this.nes.opts.onAudioSample) {
      this.nes.opts.onAudioSample(
        (sampleValueL * GAIN) / 32768,
        (sampleValueR * GAIN) / 32768,
      );
    }

    // Reset sampled values:
    this.smpSquare1 = 0;
    this.smpSquare2 = 0;
    this.smpTriangle = 0;
    this.smpDmc = 0;
  },

  getLengthMax: function (value) {
    return this.lengthLookup[value >> 3];
  },

  getDmcFrequency: function (value) {
    if (value >= 0 && value < 0x10) {
      return this.dmcFreqLookup[value];
    }
    return 0;
  },

  getNoiseWaveLength: function (value) {
    if (value >= 0 && value < 0x10) {
      return this.noiseWavelengthLookup[value];
    }
    return 0;
  },

  setPanning: function (pos) {
    for (var i = 0; i < 5; i++) {
      this.panning[i] = pos[i];
    }
    this.updateStereoPos();
  },

  setMasterVolume: function (value) {
    if (value < 0) {
      value = 0;
    }
    if (value > 256) {
      value = 256;
    }
    this.masterVolume = value;
    this.updateStereoPos();
  },

  updateStereoPos: function () {
    this.stereoPosLSquare1 = (this.panning[0] * this.masterVolume) >> 8;
    this.stereoPosLSquare2 = (this.panning[1] * this.masterVolume) >> 8;
    this.stereoPosLTriangle = (this.panning[2] * this.masterVolume) >> 8;
    this.stereoPosLNoise = (this.panning[3] * this.masterVolume) >> 8;
    this.stereoPosLDMC = (this.panning[4] * this.masterVolume) >> 8;

    this.stereoPosRSquare1 = this.masterVolume - this.stereoPosLSquare1;
    this.stereoPosRSquare2 = this.masterVolume - this.stereoPosLSquare2;
    this.stereoPosRTriangle = this.masterVolume - this.stereoPosLTriangle;
    this.stereoPosRNoise = this.masterVolume - this.stereoPosLNoise;
    this.stereoPosRDMC = this.masterVolume - this.stereoPosLDMC;
  },

  initLengthLookup: function () {
    // prettier-ignore
    this.lengthLookup = [
            0x0A, 0xFE,
            0x14, 0x02,
            0x28, 0x04,
            0x50, 0x06,
            0xA0, 0x08,
            0x3C, 0x0A,
            0x0E, 0x0C,
            0x1A, 0x0E,
            0x0C, 0x10,
            0x18, 0x12,
            0x30, 0x14,
            0x60, 0x16,
            0xC0, 0x18,
            0x48, 0x1A,
            0x10, 0x1C,
            0x20, 0x1E
        ];
  },

  initDmcFrequencyLookup: function () {
    this.dmcFreqLookup = new Array(16);

    this.dmcFreqLookup[0x0] = 0xd60;
    this.dmcFreqLookup[0x1] = 0xbe0;
    this.dmcFreqLookup[0x2] = 0xaa0;
    this.dmcFreqLookup[0x3] = 0xa00;
    this.dmcFreqLookup[0x4] = 0x8f0;
    this.dmcFreqLookup[0x5] = 0x7f0;
    this.dmcFreqLookup[0x6] = 0x710;
    this.dmcFreqLookup[0x7] = 0x6b0;
    this.dmcFreqLookup[0x8] = 0x5f0;
    this.dmcFreqLookup[0x9] = 0x500;
    this.dmcFreqLookup[0xa] = 0x470;
    this.dmcFreqLookup[0xb] = 0x400;
    this.dmcFreqLookup[0xc] = 0x350;
    this.dmcFreqLookup[0xd] = 0x2a0;
    this.dmcFreqLookup[0xe] = 0x240;
    this.dmcFreqLookup[0xf] = 0x1b0;
    //for(int i=0;i<16;i++)dmcFreqLookup[i]/=8;
  },

  initNoiseWavelengthLookup: function () {
    this.noiseWavelengthLookup = new Array(16);

    this.noiseWavelengthLookup[0x0] = 0x004;
    this.noiseWavelengthLookup[0x1] = 0x008;
    this.noiseWavelengthLookup[0x2] = 0x010;
    this.noiseWavelengthLookup[0x3] = 0x020;
    this.noiseWavelengthLookup[0x4] = 0x040;
    this.noiseWavelengthLookup[0x5] = 0x060;
    this.noiseWavelengthLookup[0x6] = 0x080;
    this.noiseWavelengthLookup[0x7] = 0x0a0;
    this.noiseWavelengthLookup[0x8] = 0x0ca;
    this.noiseWavelengthLookup[0x9] = 0x0fe;
    this.noiseWavelengthLookup[0xa] = 0x17c;
    this.noiseWavelengthLookup[0xb] = 0x1fc;
    this.noiseWavelengthLookup[0xc] = 0x2fa;
    this.noiseWavelengthLookup[0xd] = 0x3f8;
    this.noiseWavelengthLookup[0xe] = 0x7f2;
    this.noiseWavelengthLookup[0xf] = 0xfe4;
  },

  initDACtables: function () {
    var value, ival, i;
    var max_sqr = 0;
    var max_tnd = 0;
    this.square_table = new Array(32 * 16);
    this.tnd_table = new Array(204 * 16);
    for (i = 0; i < 32 * 16; i++) {
      value = 95.52 / (8128.0 / (i / 16.0) + 100.0);
      value *= 0.98411;
      value *= 50000.0;
      ival = Math.floor(value);
      this.square_table[i] = ival;
      if (ival > max_sqr) {
        max_sqr = ival;
      }
    }
    for (i = 0; i < 204 * 16; i++) {
      value = 163.67 / (24329.0 / (i / 16.0) + 100.0);
      value *= 0.98411;
      value *= 50000.0;
      ival = Math.floor(value);
      this.tnd_table[i] = ival;
      if (ival > max_tnd) {
        max_tnd = ival;
      }
    }
    this.dacRange = max_sqr + max_tnd;
    this.dcValue = this.dacRange / 2;
  },

  JSON_PROPERTIES: [
    "frameIrqCounter",
    "frameIrqCounterMax",
    "initCounter",
    "channelEnableValue",
    "sampleRate",
    "frameIrqEnabled",
    "frameIrqActive",
    "frameClockNow",
    "startedPlaying",
    "recordOutput",
    "initingHardware",
    "apuClock",
    "pendingFrameReset",
    "pendingFrameImmediate",
    "frameEvents",
    "cycleDebt",
    "countSequence",
    "sampleTimer",
    "sampleTimerMax",
    "sampleCount",
    "triValue",
    "smpSquare1",
    "smpSquare2",
    "smpTriangle",
    "smpDmc",
    "accCount",
    "prevSampleL",
    "prevSampleR",
    "smpAccumL",
    "smpAccumR",
    "masterVolume",
    "stereoPosLSquare1",
    "stereoPosLSquare2",
    "stereoPosLTriangle",
    "stereoPosLNoise",
    "stereoPosLDMC",
    "stereoPosRSquare1",
    "stereoPosRSquare2",
    "stereoPosRTriangle",
    "stereoPosRNoise",
    "stereoPosRDMC",
    "extraCycles",
    "maxSample",
    "minSample",
    "panning",
  ],

  toJSON: function () {
    let obj = utils.toJSON(this);
    obj.dmc = this.dmc.toJSON();
    obj.noise = this.noise.toJSON();
    obj.square1 = this.square1.toJSON();
    obj.square2 = this.square2.toJSON();
    obj.triangle = this.triangle.toJSON();
    return obj;
  },

  fromJSON: function (s) {
    utils.fromJSON(this, s);
    this.dmc.fromJSON(s.dmc);
    this.noise.fromJSON(s.noise);
    this.square1.fromJSON(s.square1);
    this.square2.fromJSON(s.square2);
    this.triangle.fromJSON(s.triangle);

    // States written before the cycle-exact sequencer have no apuClock or
    // frameEvents. Restart the sequence instead of poisoning every later
    // clock addition with NaN.
    if (typeof this.apuClock !== "number" || !isFinite(this.apuClock)) {
      this.apuClock = 0;
      this.pendingFrameReset = -1;
      this.pendingFrameImmediate = false;
      this.startFrame(0, false);
    }
  },
};

PAPU.prototype.addExternalSound = function (soundModule) {
  this.externalSounds.push(soundModule);
  return soundModule;
};

// Drops the sound expansion modules of the previous cartridge. A module that
// belongs to a board reads its state through the mapper, and the mapper of
// the next cartridge does not have that state: the Namco 163's module reads
// the chip's RAM, for instance, and would throw on the next frame if it
// stayed registered.
PAPU.prototype.clearExternalSounds = function () {
  this.externalSounds.length = 0;
};

// Route a cartridge write to the sound expansion modules (VRC6 uses $8000-$BFFF,
// VRC7 uses $9000-$FFFF, ...). Mappers call this instead of touching the APU.
PAPU.prototype.exWrite = function (address, value) {
  for (var i = 0; i < this.externalSounds.length; i++) {
    var sound = this.externalSounds[i];
    if (sound.handleWrite(address, value)) {
      return true;
    }
  }
  return false;
};

// Clock every registered expansion module with the given number of CPU cycles.
PAPU.prototype.clockExternalSounds = function (cycles) {
  for (var i = 0; i < this.externalSounds.length; i++) {
    this.externalSounds[i].clock(cycles);
  }
};

// Recomputes the sample timer after a sample rate change.
PAPU.prototype.setSampleRate = function (sampleRate) {
  if (sampleRate) {
    this.sampleRate = sampleRate;
  }
  this.sampleTimerMax = (1024.0 * CPU_FREQ_NTSC) / this.sampleRate;
  this.sampleTimer = 0;
};

// === VRC6 audio expansion (mappers 24 / 26) ===
//
// Three channels on one chip: two pulse generators and a sawtooth. The map
// below is VRC6a (mapper 24). The mapper swaps address bits 0 and 1 for a
// VRC6b before it gets here, so this decoder only ever sees this one layout.
//
//   $9000  pulse 1 duty/volume   $9001  period low   $9002  period high
//   $9003  frequency control: bit 0 halt, bit 1 x16, bit 2 x256
//   $A000  pulse 2 duty/volume   $A001  period low   $A002  period high
//   $B000  saw accumulator rate  $B001  period low   $B002  period high
//
// Pulse duty/volume is MDDD VVVV: bit 7 forces the duty generator on, bits 6-4
// are the duty cycle and bits 3-0 the volume. Each period pair holds twelve
// bits and bit 7 of the high byte enables the channel.
//
// The channels are clocked in CPU cycles, which papu.clockFrameCounter hands to
// clockExternalSounds() before it does any sample-rate work.

// One twelve-bit down counter per channel. It reloads with the period and
// clocks the generator on the carry, so the step rate is CPU / (period + 1).
function VRCTimer() {
  this.period = 0;
  this.counter = 0;
  this.onTick = null;
}

VRCTimer.prototype.clock = function (cycles) {
  if (this.onTick === null) {
    return;
  }
  this.counter -= cycles;
  while (this.counter < 0) {
    this.counter += this.period + 1;
    this.onTick();
  }
};

// The duty generator counts sixteen steps, down from 15 to 0, and outputs the
// volume while the step is not above the duty setting. The wiki's four duty
// cycles -- 6.25%, 12.5%, 25% and 50% -- fall out of that directly.
function VRCPulse() {
  this.dutyVolume = 0;
  this.period = 0;
  this.enabled = false;
  this.step = 0;
  this.timer = new VRCTimer();

  var self = this;
  this.timer.onTick = function () {
    self.step = (self.step - 1) & 0x0f;
  };
}

VRCPulse.prototype.writeRegister = function (offset, value) {
  switch (offset) {
    case 0:
      this.dutyVolume = value;
      break;
    case 1:
      this.period = (this.period & 0xf00) | value;
      break;
    default:
      this.enabled = (value & 0x80) !== 0;
      this.period = (this.period & 0x0ff) | ((value & 0x0f) << 8);
      if (!this.enabled) {
        // Clearing the enable resets and halts the duty generator; it starts
        // again from the top when the channel is switched back on.
        this.step = 0x0f;
        this.timer.counter = 0;
      }
      break;
  }
};

VRCPulse.prototype.generate = function () {
  if (!this.enabled) {
    return 0;
  }
  var volume = this.dutyVolume & 0x0f;
  var duty = (this.dutyVolume >> 4) & 0x07;
  var on = (this.dutyVolume & 0x80) !== 0 || this.step <= duty;
  return on ? volume * VRCPulse.SCALE : 0;
};

// The VRC6's DAC is six bits: the two pulse volumes plus the top five bits of
// the saw accumulator, with no weighting between channels. These scales put the
// three of them together at about the level of the 2A03, which papu.sample adds
// them to before its 2x gain.
VRCPulse.SCALE = 512;

// The sawtooth shares the twelve-bit divider but only reacts to every second
// carry, and the accumulator is cleared on the seventh, so one output cycle is
// fourteen carries. That is why its frequency is CPU / (14 * (period + 1)) and
// not the pulse channels' / 16.
function VRCSawtooth() {
  this.rate = 0;
  this.period = 0;
  this.enabled = false;
  this.accumulator = 0;
  this.step = 0;
  this.timer = new VRCTimer();

  var self = this;
  this.timer.onTick = function () {
    self.step++;
    if (self.step >= 14) {
      self.step = 0;
      self.accumulator = 0;
    } else if ((self.step & 1) === 0) {
      self.accumulator = (self.accumulator + self.rate) & 0xff;
    }
  };
}

VRCSawtooth.prototype.writeRegister = function (offset, value) {
  switch (offset) {
    case 0:
      this.rate = value & 0x3f;
      break;
    case 1:
      this.period = (this.period & 0xf00) | value;
      break;
    default:
      this.enabled = (value & 0x80) !== 0;
      this.period = (this.period & 0x0ff) | ((value & 0x0f) << 8);
      if (!this.enabled) {
        this.accumulator = 0;
        this.step = 0;
        this.timer.counter = 0;
      }
      break;
  }
};

VRCSawtooth.prototype.generate = function () {
  if (!this.enabled) {
    return 0;
  }
  return ((this.accumulator >> 3) & 0x1f) * VRCSawtooth.SCALE;
};

VRCSawtooth.SCALE = 256;

function VRC6Sound(nes) {
  this.nes = nes;
  this.pulse1 = new VRCPulse();
  this.pulse2 = new VRCPulse();
  this.sawtooth = new VRCSawtooth();
  this.freqControl = 0;
}

// The mapper has already undone the VRC6b swap, so these are plain VRC6a
// addresses and nothing here needs to know which board is in the slot.
VRC6Sound.prototype.handleWrite = function (address, value) {
  switch (address) {
    case 0x9000:
    case 0x9001:
    case 0x9002:
      this.pulse1.writeRegister(address - 0x9000, value);
      return true;
    case 0x9003:
      this.freqControl = value;
      return true;
    case 0xa000:
    case 0xa001:
    case 0xa002:
      this.pulse2.writeRegister(address - 0xa000, value);
      return true;
    case 0xb000:
    case 0xb001:
    case 0xb002:
      this.sawtooth.writeRegister(address - 0xb000, value);
      return true;
    default:
      return false;
  }
};

// $9003 shifts the period registers right: four bits for x16, eight for x256.
// Bit 0 halts every oscillator where it stands. All three commercial VRC6 games
// write $00 here once at startup and never touch it again.
VRC6Sound.prototype.periodShift = function () {
  if ((this.freqControl & 0x04) !== 0) {
    return 8;
  }
  if ((this.freqControl & 0x02) !== 0) {
    return 4;
  }
  return 0;
};

VRC6Sound.prototype.clock = function (cycles) {
  if ((this.freqControl & 0x01) !== 0) {
    return;
  }
  var shift = this.periodShift();
  this.pulse1.timer.period = this.pulse1.period >>> shift;
  this.pulse2.timer.period = this.pulse2.period >>> shift;
  this.sawtooth.timer.period = this.sawtooth.period >>> shift;
  this.pulse1.timer.clock(cycles);
  this.pulse2.timer.clock(cycles);
  this.sawtooth.timer.clock(cycles);
};

VRC6Sound.prototype.generate = function () {
  return (
    this.pulse1.generate() + this.pulse2.generate() + this.sawtooth.generate()
  );
};

VRC6Sound.prototype.toJSON = function () {
  return {
    freqControl: this.freqControl,
    pulse1: {
      dutyVolume: this.pulse1.dutyVolume,
      period: this.pulse1.period,
      enabled: this.pulse1.enabled,
      step: this.pulse1.step,
      counter: this.pulse1.timer.counter,
    },
    pulse2: {
      dutyVolume: this.pulse2.dutyVolume,
      period: this.pulse2.period,
      enabled: this.pulse2.enabled,
      step: this.pulse2.step,
      counter: this.pulse2.timer.counter,
    },
    sawtooth: {
      rate: this.sawtooth.rate,
      period: this.sawtooth.period,
      enabled: this.sawtooth.enabled,
      accumulator: this.sawtooth.accumulator,
      step: this.sawtooth.step,
      counter: this.sawtooth.timer.counter,
    },
  };
};

VRC6Sound.prototype.fromJSON = function (s) {
  this.freqControl = s.freqControl;
  this.pulse1.dutyVolume = s.pulse1.dutyVolume;
  this.pulse1.period = s.pulse1.period;
  this.pulse1.enabled = s.pulse1.enabled;
  this.pulse1.step = s.pulse1.step;
  this.pulse1.timer.counter = s.pulse1.counter;
  this.pulse2.dutyVolume = s.pulse2.dutyVolume;
  this.pulse2.period = s.pulse2.period;
  this.pulse2.enabled = s.pulse2.enabled;
  this.pulse2.step = s.pulse2.step;
  this.pulse2.timer.counter = s.pulse2.counter;
  this.sawtooth.rate = s.sawtooth.rate;
  this.sawtooth.period = s.sawtooth.period;
  this.sawtooth.enabled = s.sawtooth.enabled;
  this.sawtooth.accumulator = s.sawtooth.accumulator;
  this.sawtooth.step = s.sawtooth.step;
  this.sawtooth.timer.counter = s.sawtooth.counter;
};

// === Fin VRC6 ===

// === MMC5 (ExROM) ==
//
// Two pulse channels identical to the 2A03's (minus the sweep unit, whose
// register does nothing) plus a raw 8-bit PCM channel, all at $5000-$5015.
// The envelope and the length counter have no frame sequencer of their own
// and tick at a fixed 240 Hz (twice the APU length rate), with the MMC5's
// NTSC length table differing from the APU's only at index $1C.
// References: https://www.nesdev.org/wiki/MMC5_audio and Mesen's
// Core/MMC5Audio.h (SquareChannel without the sweep, 240 Hz ticks), which
// this module follows register for register.

var MMC5_LENGTH_LOOKUP = [
  10, 254, 20, 2, 40, 4, 80, 6, 160, 8, 60, 10, 14, 12, 26, 14, 12, 16, 24, 18,
  48, 20, 96, 22, 192, 24, 72, 26, 16, 28, 32, 30,
];

// Duty 0-3, the four standard 2A03 waves: 12.5%, 25%, 50% and 75%.
var MMC5_DUTY = [
  [1, 0, 0, 0, 0, 0, 0, 0],
  [1, 1, 0, 0, 0, 0, 0, 0],
  [1, 1, 1, 1, 0, 0, 0, 0],
  [1, 1, 1, 1, 1, 1, 0, 0],
];

function MMC5Pulse() {
  this.duty = 0;
  // $5000 bit 5: halts the length counter and loops the envelope.
  this.halted = false;
  this.constantVolume = false;
  // Bits 3-0 of $5000: the raw volume, and the envelope divider period.
  this.volume = 0;
  // The 11-bit period register. The timer runs twice as fast as the raw
  // period states (the 2A03 timers are clocked every second CPU cycle), so
  // the reload passed to VRCTimer is 2 * period + 1.
  this.period = 0;
  this.enabled = false;
  this.lengthCounter = 0;
  this.envelopeCounter = 0;
  this.envelopeDivider = 0;
  this.envelopeRestart = false;
  this.step = 0;
  this.timer = new VRCTimer();

  var self = this;
  this.timer.onTick = function () {
    self.step = (self.step + 1) & 7;
  };
}

MMC5Pulse.prototype.writeRegister = function (offset, value) {
  switch (offset) {
    case 0:
      this.duty = (value >> 6) & 3;
      this.halted = (value & 0x20) !== 0;
      this.constantVolume = (value & 0x10) !== 0;
      this.volume = value & 0x0f;
      this.envelopeRestart = true;
      break;
    case 1:
      // No sweep unit: $5001 does nothing.
      break;
    case 2:
      this.period = (this.period & 0x700) | value;
      break;
    default: {
      // $5003/$5007: the high period bits and the length counter load. Bit 7
      // is the sound flag. A write with it clear silences the channel; the
      // $5015 bits gate the same flag from the CPU side.
      this.period = (this.period & 0xff) | ((value & 7) << 8);
      if (value & 0x80) {
        this.enabled = true;
        this.lengthCounter = MMC5_LENGTH_LOOKUP[value >> 3];
      } else {
        this.enabled = false;
        this.lengthCounter = 0;
      }
      // Phase reset: the duty sequencer and the timer restart, and the
      // envelope with them.
      this.step = 0;
      this.timer.counter = 0;
      this.envelopeRestart = true;
      break;
    }
  }

  // The 2A03-style timer is clocked every second CPU cycle, so a raw period
  // of t makes one duty step last 2t+1 CPU cycles. VRCTimer reloads with
  // period + 1, hence the doubling.
  this.timer.period = this.period * 2;
};

// $5015: the two bits enable the channels like the APU's $4015; clearing a
// bit zeroes the length counter.
MMC5Pulse.prototype.setEnabled = function (on) {
  if (on) {
    this.enabled = true;
  } else {
    this.enabled = false;
    this.lengthCounter = 0;
  }
};

// The 240 Hz tick: length counter first, then the envelope.
MMC5Pulse.prototype.tick240 = function () {
  if (this.lengthCounter > 0 && !this.halted) {
    this.lengthCounter--;
  }

  if (this.envelopeRestart) {
    this.envelopeRestart = false;
    this.envelopeCounter = 15;
    this.envelopeDivider = this.volume;
  } else {
    this.envelopeDivider--;
    if (this.envelopeDivider < 0) {
      this.envelopeDivider = this.volume;
      if (this.envelopeCounter > 0) {
        this.envelopeCounter--;
      } else if (this.halted) {
        this.envelopeCounter = 15;
      }
    }
  }
};

MMC5Pulse.prototype.generate = function () {
  if (!this.enabled || this.lengthCounter === 0) {
    return 0;
  }
  var level = this.constantVolume ? this.volume : this.envelopeCounter;
  if (level === 0) {
    return 0;
  }
  // Frequency values below 8 do NOT silence an MMC5 pulse, unlike the 2A03.
  return MMC5_DUTY[this.duty][this.step] ? level * MMC5Pulse.SCALE : 0;
};

// "The sound output of the square channels are equivalent in volume to the
// corresponding APU channels": a full-volume step lands next to a full
// 2A03 pulse step (sampleValue 15, which this APU mixes to ~2600 units).
MMC5Pulse.SCALE = 176;

function MMC5Sound(nes) {
  this.nes = nes;
  this.pulse1 = new MMC5Pulse();
  this.pulse2 = new MMC5Pulse();
  this.pcmReadMode = false;
  this.pcmIrqEnabled = false;
  this.pcmIrqTrip = false;
  this.pcmOutput = 0;
  this.pcmLine = 0;
  this.irqOnCpu = false;
  // The fixed 240 Hz envelope/length clock. NTSC only, like every other
  // rate constant in this APU.
  this.envAccum = 0;
  this.envPeriod = CPU_FREQ_NTSC / 240;
}

// The mapper forwards $5000-$5015 writes to papu.exWrite(), which lands here.
MMC5Sound.prototype.handleWrite = function (address, value) {
  if (address >= 0x5000 && address <= 0x5003) {
    this.pulse1.writeRegister(address - 0x5000, value);
    return true;
  }
  if (address >= 0x5004 && address <= 0x5007) {
    this.pulse2.writeRegister(address - 0x5004, value);
    return true;
  }
  switch (address) {
    case 0x5010:
      // Mode select (bit 0) and PCM IRQ enable (bit 7).
      this.pcmReadMode = (value & 0x01) !== 0;
      this.pcmIrqEnabled = (value & 0x80) !== 0;
      this.refreshCpuIrq();
      return true;
    case 0x5011:
      if (!this.pcmReadMode) {
        // Writing $00 leaves the DAC alone and just trips the IRQ.
        if (value !== 0) {
          this.pcmOutput = value;
        } else {
          this.pcmIrqTrip = true;
          this.refreshCpuIrq();
        }
      }
      return true;
    case 0x5015:
      this.pulse1.setEnabled((value & 0x01) !== 0);
      this.pulse2.setEnabled((value & 0x02) !== 0);
      return true;
    default:
      return false;
  }
};

// Reads of $5010 and $5015 come from the mapper, since papu has no read path
// for expansion modules.
MMC5Sound.prototype.statusRead = function (address) {
  if (address === 0x5010) {
    // Bit 7 reports the pending PCM IRQ and reading acknowledges it; bit 0
    // is the chip revision (1 = MMC5A, the revision every cart carries).
    var status = (this.pcmIrqEnabled && this.pcmIrqTrip ? 0x80 : 0) | 0x01;
    this.pcmIrqTrip = false;
    this.refreshCpuIrq();
    return status;
  }
  if (address === 0x5015) {
    return (
      (this.pulse1.lengthCounter > 0 ? 0x01 : 0) |
      (this.pulse2.lengthCounter > 0 ? 0x02 : 0)
    );
  }
  return 0;
};

// PCM read mode ($5010 bit 0 set): every CPU read from $8000-$BFFF becomes a
// sample fetch. The mapper calls this with the value it read.
MMC5Sound.prototype.onPcmFetch = function (value) {
  if (this.pcmReadMode) {
    if (value !== 0) {
      this.pcmOutput = value;
    } else {
      this.pcmIrqTrip = true;
      this.refreshCpuIrq();
    }
  }
};

// The MMC5 has one IRQ line, shared with the scanline IRQ the mapper raises,
// so the same latched-request dance applies: the request is put up while the
// flag is tripped and taken back down when it is acknowledged.
MMC5Sound.prototype.refreshCpuIrq = function () {
  var cpu = this.nes.cpu;

  if (this.pcmIrqEnabled && this.pcmIrqTrip) {
    if (!this.irqOnCpu) {
      cpu.requestIrq(cpu.IRQ_NORMAL);
      this.irqOnCpu = true;
    }
    return;
  }

  if (this.irqOnCpu && cpu.irqRequested && cpu.irqType === cpu.IRQ_NORMAL) {
    cpu.irqRequested = false;
  }
  this.irqOnCpu = false;
};

MMC5Sound.prototype.reset = function () {
  this.pulse1 = new MMC5Pulse();
  this.pulse2 = new MMC5Pulse();
  this.pcmReadMode = false;
  this.pcmIrqEnabled = false;
  this.pcmIrqTrip = false;
  this.pcmOutput = 0;
  this.irqOnCpu = false;
  this.envAccum = 0;
};

MMC5Sound.prototype.clock = function (cycles) {
  // The timers run at CPU rate; the envelope and length counter at a fixed
  // 240 Hz (there is no frame sequencer to follow, even in 5-step mode).
  this.pulse1.timer.clock(cycles);
  this.pulse2.timer.clock(cycles);

  this.envAccum += cycles;
  while (this.envAccum >= this.envPeriod) {
    this.envAccum -= this.envPeriod;
    this.pulse1.tick240();
    this.pulse2.tick240();
  }
};

// $5011 is an 8-bit DAC where the APU's $4011 takes 7 bits, so a full-scale
// write is twice the equivalent DMC level. The pulses sit at the 2A03's own
// level rather than the VRC6's louder one.
MMC5Sound.prototype.generate = function () {
  return this.pulse1.generate() + this.pulse2.generate() + this.pcmOutput * 133;
};

MMC5Sound.prototype.toJSON = function () {
  return {
    pulse1: {
      duty: this.pulse1.duty,
      halted: this.pulse1.halted,
      constantVolume: this.pulse1.constantVolume,
      volume: this.pulse1.volume,
      period: this.pulse1.period,
      enabled: this.pulse1.enabled,
      lengthCounter: this.pulse1.lengthCounter,
      envelopeCounter: this.pulse1.envelopeCounter,
      envelopeDivider: this.pulse1.envelopeDivider,
      envelopeRestart: this.pulse1.envelopeRestart,
      step: this.pulse1.step,
      counter: this.pulse1.timer.counter,
    },
    pulse2: {
      duty: this.pulse2.duty,
      halted: this.pulse2.halted,
      constantVolume: this.pulse2.constantVolume,
      volume: this.pulse2.volume,
      period: this.pulse2.period,
      enabled: this.pulse2.enabled,
      lengthCounter: this.pulse2.lengthCounter,
      envelopeCounter: this.pulse2.envelopeCounter,
      envelopeDivider: this.pulse2.envelopeDivider,
      envelopeRestart: this.pulse2.envelopeRestart,
      step: this.pulse2.step,
      counter: this.pulse2.timer.counter,
    },
    pcmReadMode: this.pcmReadMode,
    pcmIrqEnabled: this.pcmIrqEnabled,
    pcmIrqTrip: this.pcmIrqTrip,
    pcmOutput: this.pcmOutput,
    irqOnCpu: this.irqOnCpu,
    envAccum: this.envAccum,
  };
};

MMC5Sound.prototype.fromJSON = function (s) {
  var pulse;
  for (var i = 1; i <= 2; i++) {
    pulse = s["pulse" + i];
    if (!pulse) {
      continue;
    }
    var target = this["pulse" + i];
    target.duty = pulse.duty;
    target.halted = pulse.halted;
    target.constantVolume = pulse.constantVolume;
    target.volume = pulse.volume;
    target.period = pulse.period;
    target.enabled = pulse.enabled;
    target.lengthCounter = pulse.lengthCounter;
    target.envelopeCounter = pulse.envelopeCounter;
    target.envelopeDivider = pulse.envelopeDivider;
    target.envelopeRestart = pulse.envelopeRestart;
    target.step = pulse.step;
    target.timer.counter = pulse.counter;
  }
  this.pcmReadMode = s.pcmReadMode;
  this.pcmIrqEnabled = s.pcmIrqEnabled;
  this.pcmIrqTrip = s.pcmIrqTrip;
  this.pcmOutput = s.pcmOutput;
  this.irqOnCpu = s.irqOnCpu;
  this.envAccum = s.envAccum;
};

// === Fin MMC5 ===

var ChannelDM = function (papu) {
  this.papu = papu;

  this.MODE_NORMAL = 0;
  this.MODE_LOOP = 1;
  this.MODE_IRQ = 2;

  this.isEnabled = null;
  this.hasSample = null;
  this.irqGenerated = false;

  this.playMode = null;
  this.dmaFrequency = null;
  this.dmaCounter = null;
  this.deltaCounter = null;
  this.playStartAddress = null;
  this.playAddress = null;
  this.playLength = null;
  this.playLengthCounter = null;
  this.shiftCounter = null;
  this.reg4012 = null;
  this.reg4013 = null;
  this.sample = null;
  this.dacLsb = null;
  this.data = null;

  this.reset();
};

ChannelDM.prototype = {
  clockDmc: function () {
    // Only alter DAC value if the sample buffer has data:
    if (this.hasSample) {
      if ((this.data & 1) === 0) {
        // Decrement delta:
        if (this.deltaCounter > 0) {
          this.deltaCounter--;
        }
      } else {
        // Increment delta:
        if (this.deltaCounter < 63) {
          this.deltaCounter++;
        }
      }

      // Update sample value:
      this.sample = this.isEnabled ? (this.deltaCounter << 1) + this.dacLsb : 0;

      // Update shift register:
      this.data >>= 1;
    }

    this.dmaCounter--;
    if (this.dmaCounter <= 0) {
      // No more sample bits.
      this.hasSample = false;
      this.endOfSample();
      this.dmaCounter = 8;
    }

    if (this.irqGenerated) {
      this.papu.nes.cpu.requestIrq(this.papu.nes.cpu.IRQ_NORMAL);
    }
  },

  endOfSample: function () {
    // The shift register has consumed the buffered byte. Fetch the next one
    // if the memory reader still has bytes to read.
    if (this.playLengthCounter > 0) {
      this.nextSample();
    }
  },

  nextSample: function () {
    // Fetch byte:
    this.data = this.papu.nes.mmap.load(this.playAddress);
    this.papu.nes.cpu.haltCycles(4);

    this.playLengthCounter--;
    this.playAddress++;
    if (this.playAddress > 0xffff) {
      this.playAddress = 0x8000;
    }

    this.hasSample = true;
    this.dmaCounter = 8;

    if (this.playLengthCounter === 0) {
      if (this.playMode === this.MODE_LOOP) {
        // The loop restarts as soon as the final byte is fetched, not when
        // its last bit has been played: bytes-remaining is therefore never
        // zero while a loop is running.
        this.playAddress = this.playStartAddress;
        this.playLengthCounter = this.playLength;
      } else if (this.playMode === this.MODE_IRQ) {
        // The DMC interrupt flag is raised as soon as the final byte is
        // fetched, not when that byte's last bit has finished playing.
        this.irqGenerated = true;
      }
    }
  },

  writeReg: function (address, value) {
    if (address === 0x4010) {
      // Play mode, DMA Frequency
      if (value >> 6 === 0) {
        this.playMode = this.MODE_NORMAL;
      } else if (((value >> 6) & 1) === 1) {
        this.playMode = this.MODE_LOOP;
      } else if (value >> 6 === 2) {
        this.playMode = this.MODE_IRQ;
      }

      if ((value & 0x80) === 0) {
        this.irqGenerated = false;
      }

      this.dmaFrequency = this.papu.getDmcFrequency(value & 0xf);
    } else if (address === 0x4011) {
      // Delta counter load register:
      this.deltaCounter = (value >> 1) & 63;
      this.dacLsb = value & 1;
      this.sample = (this.deltaCounter << 1) + this.dacLsb; // update sample value
    } else if (address === 0x4012) {
      // DMA address load register
      this.playStartAddress = (value << 6) | 0x0c000;
      this.playAddress = this.playStartAddress;
      this.reg4012 = value;
    } else if (address === 0x4013) {
      // Length of play code. Only the length for the next load changes: the
      // sample already playing keeps its own bytes-remaining counter, and a
      // loop uses the latest value when it reloads.
      this.playLength = (value << 4) + 1;
      this.reg4013 = value;
    } else if (address === 0x4015) {
      // DMC/IRQ Status. Writing here always clears the sample-end IRQ flag;
      // it is cleared before a possible restart so that the DMC DMA that
      // fetches a one-byte sample can raise it again.
      this.irqGenerated = false;

      if (((value >> 4) & 1) === 0) {
        // Disable:
        this.playLengthCounter = 0;
      } else if (this.playLengthCounter === 0) {
        // (Re)start only when nothing is queued. Writing 1 while a sample is
        // still playing must be ignored.
        this.playAddress = this.playStartAddress;
        this.playLengthCounter = this.playLength;
        if (!this.hasSample) {
          // The one-byte buffer is filled immediately when the sample
          // starts; this is also what raises the IRQ for a one-byte sample.
          this.nextSample();
        }
      }
    }
  },

  setEnabled: function (value) {
    this.isEnabled = value;
  },

  getLengthStatus: function () {
    return this.playLengthCounter === 0 || !this.isEnabled ? 0 : 1;
  },

  getIrqStatus: function () {
    return this.irqGenerated ? 1 : 0;
  },

  reset: function () {
    this.isEnabled = false;
    this.irqGenerated = false;
    this.playMode = this.MODE_NORMAL;
    this.dmaFrequency = 0;
    this.dmaCounter = 0;
    this.deltaCounter = 0;
    this.playStartAddress = 0;
    this.playAddress = 0;
    this.playLength = 0;
    this.playLengthCounter = 0;
    this.sample = 0;
    this.dacLsb = 0;
    this.shiftCounter = 0;
    this.reg4012 = 0;
    this.reg4013 = 0;
    this.data = 0;
  },

  JSON_PROPERTIES: [
    "MODE_NORMAL",
    "MODE_LOOP",
    "MODE_IRQ",
    "isEnabled",
    "hasSample",
    "irqGenerated",
    "playMode",
    "dmaFrequency",
    "dmaCounter",
    "deltaCounter",
    "playStartAddress",
    "playAddress",
    "playLength",
    "playLengthCounter",
    "shiftCounter",
    "reg4012",
    "reg4013",
    "sample",
    "dacLsb",
    "data",
  ],

  toJSON: function () {
    return utils.toJSON(this);
  },

  fromJSON: function (s) {
    utils.fromJSON(this, s);
  },
};

var ChannelNoise = function (papu) {
  this.papu = papu;

  this.isEnabled = null;
  this.envDecayDisable = null;
  this.envDecayLoopEnable = null;
  this.lengthCounterEnable = null;
  this.envReset = null;
  this.shiftNow = null;

  this.lengthCounter = null;
  this.progTimerCount = null;
  this.progTimerMax = null;
  this.envDecayRate = null;
  this.envDecayCounter = null;
  this.envVolume = null;
  this.masterVolume = null;
  this.shiftReg = 1 << 14;
  this.randomBit = null;
  this.randomMode = null;
  this.sampleValue = null;
  this.accValue = 0;
  this.accCount = 1;
  this.tmp = null;

  this.reset();
};

ChannelNoise.prototype = {
  reset: function () {
    this.progTimerCount = 0;
    this.progTimerMax = 0;
    this.isEnabled = false;
    this.lengthCounter = 0;
    this.lengthCounterEnable = false;
    this.envDecayDisable = false;
    this.envDecayLoopEnable = false;
    this.shiftNow = false;
    this.envDecayRate = 0;
    this.envDecayCounter = 0;
    this.envVolume = 0;
    this.masterVolume = 0;
    this.shiftReg = 1;
    this.randomBit = 0;
    this.randomMode = 0;
    this.sampleValue = 0;
    this.tmp = 0;
  },

  clockLengthCounter: function () {
    if (this.lengthCounterEnable && this.lengthCounter > 0) {
      this.lengthCounter--;
      if (this.lengthCounter === 0) {
        this.updateSampleValue();
      }
    }
  },

  clockEnvDecay: function () {
    if (this.envReset) {
      // Reset envelope:
      this.envReset = false;
      this.envDecayCounter = this.envDecayRate + 1;
      this.envVolume = 0xf;
    } else if (--this.envDecayCounter <= 0) {
      // Normal handling:
      this.envDecayCounter = this.envDecayRate + 1;
      if (this.envVolume > 0) {
        this.envVolume--;
      } else {
        this.envVolume = this.envDecayLoopEnable ? 0xf : 0;
      }
    }
    if (this.envDecayDisable) {
      this.masterVolume = this.envDecayRate;
    } else {
      this.masterVolume = this.envVolume;
    }
    this.updateSampleValue();
  },

  updateSampleValue: function () {
    if (this.isEnabled && this.lengthCounter > 0) {
      this.sampleValue = this.randomBit * this.masterVolume;
    }
  },

  writeReg: function (address, value) {
    if (address === 0x400c) {
      // Volume/Envelope decay:
      this.envDecayDisable = (value & 0x10) !== 0;
      this.envDecayRate = value & 0xf;
      this.envDecayLoopEnable = (value & 0x20) !== 0;
      this.lengthCounterEnable = (value & 0x20) === 0;
      if (this.envDecayDisable) {
        this.masterVolume = this.envDecayRate;
      } else {
        this.masterVolume = this.envVolume;
      }
    } else if (address === 0x400e) {
      // Programmable timer:
      this.progTimerMax = this.papu.getNoiseWaveLength(value & 0xf);
      this.randomMode = value >> 7;
    } else if (address === 0x400f) {
      // Length counter
      // A disabled channel ignores the length load ($4015 bit 3 clear).
      if (this.isEnabled) {
        this.lengthCounter = this.papu.getLengthMax(value & 248);
      }
      this.envReset = true;
    }
    // Update:
    //updateSampleValue();
  },

  setEnabled: function (value) {
    this.isEnabled = value;
    if (!value) {
      this.lengthCounter = 0;
    }
    this.updateSampleValue();
  },

  getLengthStatus: function () {
    return this.lengthCounter === 0 || !this.isEnabled ? 0 : 1;
  },

  JSON_PROPERTIES: [
    "isEnabled",
    "envDecayDisable",
    "envDecayLoopEnable",
    "lengthCounterEnable",
    "envReset",
    "shiftNow",
    "lengthCounter",
    "progTimerCount",
    "progTimerMax",
    "envDecayRate",
    "envDecayCounter",
    "envVolume",
    "masterVolume",
    "shiftReg",
    "randomBit",
    "randomMode",
    "sampleValue",
    "accValue",
    "accCount",
    "tmp",
  ],

  toJSON: function () {
    return utils.toJSON(this);
  },

  fromJSON: function (s) {
    utils.fromJSON(this, s);
  },
};

var ChannelSquare = function (papu, square1) {
  this.papu = papu;

  // prettier-ignore
  this.dutyLookup = [
         0, 1, 0, 0, 0, 0, 0, 0,
         0, 1, 1, 0, 0, 0, 0, 0,
         0, 1, 1, 1, 1, 0, 0, 0,
         1, 0, 0, 1, 1, 1, 1, 1
    ];
  // prettier-ignore
  this.impLookup = [
         1,-1, 0, 0, 0, 0, 0, 0,
         1, 0,-1, 0, 0, 0, 0, 0,
         1, 0, 0, 0,-1, 0, 0, 0,
        -1, 0, 1, 0, 0, 0, 0, 0
    ];

  this.sqr1 = square1;
  this.isEnabled = null;
  this.lengthCounterEnable = null;
  this.sweepActive = null;
  this.envDecayDisable = null;
  this.envDecayLoopEnable = null;
  this.envReset = null;
  this.sweepCarry = null;
  this.updateSweepPeriod = null;

  this.progTimerCount = null;
  this.progTimerMax = null;
  this.lengthCounter = null;
  this.squareCounter = null;
  this.sweepCounter = null;
  this.sweepCounterMax = null;
  this.sweepMode = null;
  this.sweepShiftAmount = null;
  this.envDecayRate = null;
  this.envDecayCounter = null;
  this.envVolume = null;
  this.masterVolume = null;
  this.dutyMode = null;
  this.sweepResult = null;
  this.sampleValue = null;
  this.vol = null;

  this.reset();
};

ChannelSquare.prototype = {
  reset: function () {
    this.progTimerCount = 0;
    this.progTimerMax = 0;
    this.lengthCounter = 0;
    this.squareCounter = 0;
    this.sweepCounter = 0;
    this.sweepCounterMax = 0;
    this.sweepMode = 0;
    this.sweepShiftAmount = 0;
    this.envDecayRate = 0;
    this.envDecayCounter = 0;
    this.envVolume = 0;
    this.masterVolume = 0;
    this.dutyMode = 0;
    this.vol = 0;

    this.isEnabled = false;
    this.lengthCounterEnable = false;
    this.sweepActive = false;
    this.sweepCarry = false;
    this.envDecayDisable = false;
    this.envDecayLoopEnable = false;
  },

  clockLengthCounter: function () {
    if (this.lengthCounterEnable && this.lengthCounter > 0) {
      this.lengthCounter--;
      if (this.lengthCounter === 0) {
        this.updateSampleValue();
      }
    }
  },

  clockEnvDecay: function () {
    if (this.envReset) {
      // Reset envelope:
      this.envReset = false;
      this.envDecayCounter = this.envDecayRate + 1;
      this.envVolume = 0xf;
    } else if (--this.envDecayCounter <= 0) {
      // Normal handling:
      this.envDecayCounter = this.envDecayRate + 1;
      if (this.envVolume > 0) {
        this.envVolume--;
      } else {
        this.envVolume = this.envDecayLoopEnable ? 0xf : 0;
      }
    }

    if (this.envDecayDisable) {
      this.masterVolume = this.envDecayRate;
    } else {
      this.masterVolume = this.envVolume;
    }
    this.updateSampleValue();
  },

  clockSweep: function () {
    if (--this.sweepCounter <= 0) {
      this.sweepCounter = this.sweepCounterMax + 1;
      if (
        this.sweepActive &&
        this.sweepShiftAmount > 0 &&
        this.progTimerMax > 7
      ) {
        // Calculate result from shifter:
        this.sweepCarry = false;
        if (this.sweepMode === 0) {
          this.progTimerMax += this.progTimerMax >> this.sweepShiftAmount;
          if (this.progTimerMax > 4095) {
            this.progTimerMax = 4095;
            this.sweepCarry = true;
          }
        } else {
          this.progTimerMax =
            this.progTimerMax -
            ((this.progTimerMax >> this.sweepShiftAmount) -
              (this.sqr1 ? 1 : 0));
        }
      }
    }

    if (this.updateSweepPeriod) {
      this.updateSweepPeriod = false;
      this.sweepCounter = this.sweepCounterMax + 1;
    }
  },

  updateSampleValue: function () {
    if (this.isEnabled && this.lengthCounter > 0 && this.progTimerMax > 7) {
      if (
        this.sweepMode === 0 &&
        this.progTimerMax + (this.progTimerMax >> this.sweepShiftAmount) > 4095
      ) {
        //if (this.sweepCarry) {
        this.sampleValue = 0;
      } else {
        this.sampleValue =
          this.masterVolume *
          this.dutyLookup[(this.dutyMode << 3) + this.squareCounter];
      }
    } else {
      this.sampleValue = 0;
    }
  },

  writeReg: function (address, value) {
    var addrAdd = this.sqr1 ? 0 : 4;
    if (address === 0x4000 + addrAdd) {
      // Volume/Envelope decay:
      this.envDecayDisable = (value & 0x10) !== 0;
      this.envDecayRate = value & 0xf;
      this.envDecayLoopEnable = (value & 0x20) !== 0;
      this.dutyMode = (value >> 6) & 0x3;
      this.lengthCounterEnable = (value & 0x20) === 0;
      if (this.envDecayDisable) {
        this.masterVolume = this.envDecayRate;
      } else {
        this.masterVolume = this.envVolume;
      }
      this.updateSampleValue();
    } else if (address === 0x4001 + addrAdd) {
      // Sweep:
      this.sweepActive = (value & 0x80) !== 0;
      this.sweepCounterMax = (value >> 4) & 7;
      this.sweepMode = (value >> 3) & 1;
      this.sweepShiftAmount = value & 7;
      this.updateSweepPeriod = true;
    } else if (address === 0x4002 + addrAdd) {
      // Programmable timer:
      this.progTimerMax &= 0x700;
      this.progTimerMax |= value;
    } else if (address === 0x4003 + addrAdd) {
      // Programmable timer, length counter
      this.progTimerMax &= 0xff;
      this.progTimerMax |= (value & 0x7) << 8;

      if (this.isEnabled) {
        this.lengthCounter = this.papu.getLengthMax(value & 0xf8);
      }

      this.envReset = true;
    }
  },

  setEnabled: function (value) {
    this.isEnabled = value;
    if (!value) {
      this.lengthCounter = 0;
    }
    this.updateSampleValue();
  },

  getLengthStatus: function () {
    return this.lengthCounter === 0 || !this.isEnabled ? 0 : 1;
  },

  JSON_PROPERTIES: [
    "isEnabled",
    "lengthCounterEnable",
    "sweepActive",
    "envDecayDisable",
    "envDecayLoopEnable",
    "envReset",
    "sweepCarry",
    "updateSweepPeriod",
    "progTimerCount",
    "progTimerMax",
    "lengthCounter",
    "squareCounter",
    "sweepCounter",
    "sweepCounterMax",
    "sweepMode",
    "sweepShiftAmount",
    "envDecayRate",
    "envDecayCounter",
    "envVolume",
    "masterVolume",
    "dutyMode",
    "sweepResult",
    "sampleValue",
    "vol",
  ],

  toJSON: function () {
    return utils.toJSON(this);
  },

  fromJSON: function (s) {
    utils.fromJSON(this, s);
  },
};

var ChannelTriangle = function (papu) {
  this.papu = papu;

  this.isEnabled = null;
  this.sampleCondition = null;
  this.lengthCounterEnable = null;
  this.lcHalt = null;
  this.lcControl = null;

  this.progTimerCount = null;
  this.progTimerMax = null;
  this.triangleCounter = null;
  this.lengthCounter = null;
  this.linearCounter = null;
  this.lcLoadValue = null;
  this.sampleValue = null;
  this.tmp = null;

  this.reset();
};

ChannelTriangle.prototype = {
  reset: function () {
    this.progTimerCount = 0;
    this.progTimerMax = 0;
    this.triangleCounter = 0;
    this.isEnabled = false;
    this.sampleCondition = false;
    this.lengthCounter = 0;
    this.lengthCounterEnable = false;
    this.linearCounter = 0;
    this.lcLoadValue = 0;
    this.lcHalt = true;
    this.lcControl = false;
    this.tmp = 0;
    this.sampleValue = 0xf;
  },

  clockLengthCounter: function () {
    if (this.lengthCounterEnable && this.lengthCounter > 0) {
      this.lengthCounter--;
      if (this.lengthCounter === 0) {
        this.updateSampleCondition();
      }
    }
  },

  clockLinearCounter: function () {
    if (this.lcHalt) {
      // Load:
      this.linearCounter = this.lcLoadValue;
      this.updateSampleCondition();
    } else if (this.linearCounter > 0) {
      // Decrement:
      this.linearCounter--;
      this.updateSampleCondition();
    }
    if (!this.lcControl) {
      // Clear halt flag:
      this.lcHalt = false;
    }
  },

  getLengthStatus: function () {
    return this.lengthCounter === 0 || !this.isEnabled ? 0 : 1;
  },

  // eslint-disable-next-line no-unused-vars
  readReg: function (address) {
    return 0;
  },

  writeReg: function (address, value) {
    if (address === 0x4008) {
      // New values for linear counter:
      this.lcControl = (value & 0x80) !== 0;
      this.lcLoadValue = value & 0x7f;

      // Length counter enable:
      this.lengthCounterEnable = !this.lcControl;
    } else if (address === 0x400a) {
      // Programmable timer:
      this.progTimerMax &= 0x700;
      this.progTimerMax |= value;
    } else if (address === 0x400b) {
      // Programmable timer, length counter
      this.progTimerMax &= 0xff;
      this.progTimerMax |= (value & 0x07) << 8;
      // A disabled channel ignores the length load ($4015 bit 2 clear).
      if (this.isEnabled) {
        this.lengthCounter = this.papu.getLengthMax(value & 0xf8);
      }
      this.lcHalt = true;
    }

    this.updateSampleCondition();
  },

  clockProgrammableTimer: function (nCycles) {
    if (this.progTimerMax > 0) {
      this.progTimerCount += nCycles;
      while (
        this.progTimerMax > 0 &&
        this.progTimerCount >= this.progTimerMax
      ) {
        this.progTimerCount -= this.progTimerMax;
        if (
          this.isEnabled &&
          this.lengthCounter > 0 &&
          this.linearCounter > 0
        ) {
          this.clockTriangleGenerator();
        }
      }
    }
  },

  clockTriangleGenerator: function () {
    this.triangleCounter++;
    this.triangleCounter &= 0x1f;
  },

  setEnabled: function (value) {
    this.isEnabled = value;
    if (!value) {
      this.lengthCounter = 0;
    }
    this.updateSampleCondition();
  },

  updateSampleCondition: function () {
    this.sampleCondition =
      this.isEnabled &&
      this.progTimerMax > 7 &&
      this.linearCounter > 0 &&
      this.lengthCounter > 0;
  },

  JSON_PROPERTIES: [
    "isEnabled",
    "sampleCondition",
    "lengthCounterEnable",
    "lcHalt",
    "lcControl",
    "progTimerCount",
    "progTimerMax",
    "triangleCounter",
    "lengthCounter",
    "linearCounter",
    "lcLoadValue",
    "sampleValue",
    "tmp",
  ],

  toJSON: function () {
    return utils.toJSON(this);
  },

  fromJSON: function (s) {
    utils.fromJSON(this, s);
  },
};

// === Namco 163 audio expansion (mapper 19) ===
//
// The Namco 163 -- the chip behind iNES mapper 19 -- has up to eight sound
// channels whose waveform is a table of 4-bit samples in the chip's own 128
// bytes of RAM. The first 32 bytes of that RAM are the wave table (two
// samples per byte); the rest holds the parameters of the channels:
//
//   $40 + channel * 8   frequency low        phase low
//                       frequency middle     phase middle
//                       frequency high       phase high
//                       wave address        volume
//
// The number of channels is in the high nybble of the byte at $7F. Each
// channel runs a phase accumulator over the wave table, and the channels are
// updated round robin, one every sixteen CPU cycles.
//
// The RAM belongs to the cartridge, so the module reads it from the mapper
// rather than keeping a copy of its own.

function Namco163Sound(nes) {
  this.nes = nes;
  this.output = [0, 0, 0, 0, 0, 0, 0, 0];
  this.updateCounter = 0;
  this.currentChannel = 7;
  this.disableSound = false;
}

Namco163Sound.prototype.reset = function () {
  for (var i = 0; i < 8; i++) {
    this.output[i] = 0;
  }
  this.updateCounter = 0;
  this.currentChannel = 7;
  this.disableSound = false;
};

// The chip's RAM, which the mapper owns. It is missing when the cartridge
// has been replaced under the module's feet, so an empty array keeps a
// leftover module harmless instead of throwing.
Namco163Sound.prototype.ram = function () {
  var mapper = this.nes && this.nes.mmap;
  if (!mapper || !mapper.chipRam) {
    return Namco163Sound.EMPTY_RAM;
  }
  return mapper.chipRam;
};

Namco163Sound.EMPTY_RAM = new Uint8Array(0x80);

Namco163Sound.prototype.channelCount = function () {
  return (this.ram()[0x7f] >> 4) & 0x07;
};

Namco163Sound.prototype.updateChannel = function (channel) {
  var ram = this.ram();
  var base = 0x40 + channel * 0x08;

  var frequency =
    ((ram[base + 4] & 0x03) << 16) | (ram[base + 2] << 8) | ram[base + 0];
  var phase = (ram[base + 5] << 16) | (ram[base + 3] << 8) | ram[base + 1];
  var length = 256 - (ram[base + 4] & 0xfc);
  var offset = ram[base + 6];
  var volume = ram[base + 7] & 0x0f;

  phase = (phase + frequency) % (length << 16);

  var position = ((phase >> 16) + offset) & 0xff;
  var sample =
    position & 1 ? ram[position >> 1] >> 4 : ram[position >> 1] & 0x0f;

  this.output[channel] = (sample - 8) * volume;

  // The phase lives in the RAM, so it is written back: a game that rewrites
  // the phase bytes takes the channel back at that point.
  ram[base + 5] = (phase >> 16) & 0xff;
  ram[base + 3] = (phase >> 8) & 0xff;
  ram[base + 1] = phase & 0xff;
};

Namco163Sound.prototype.clock = function (cycles) {
  if (this.disableSound) {
    return;
  }
  this.updateCounter += cycles;
  while (this.updateCounter >= 16) {
    this.updateCounter -= 16;
    this.updateChannel(this.currentChannel);
    this.currentChannel--;
    if (this.currentChannel < 7 - this.channelCount()) {
      this.currentChannel = 7;
    }
  }
};

Namco163Sound.prototype.generate = function () {
  var count = this.channelCount();
  if (count === 0) {
    return 0;
  }
  var sum = 0;
  for (var i = 7; i >= 7 - count; i--) {
    sum += this.output[i];
  }
  return Math.floor(sum / (count + 1)) * Namco163Sound.SCALE;
};

// The N163's DAC is four bits wide against a four-bit volume, so one channel
// at full volume reaches about the level of one 2A03 channel.
Namco163Sound.SCALE = 64;

module.exports = PAPU;
module.exports.PAPU = PAPU;
module.exports.VRC6Sound = VRC6Sound;
module.exports.MMC5Sound = MMC5Sound;
module.exports.Namco163Sound = Namco163Sound;
